<template>
  <section class="video-block">
    <div class="container">
      <h2
        v-if="values.heading"
        class="video-block__title"
        v-html="values.heading"
      />
    </div>
    <div
      ref="videoWrapRef"
      class="video-block__video"
    >
      <video
        v-if="isShowVideoFrame"
        ref="refVideo"
        loop
        playsinline
        preload="none"
        :controls="false"
        :poster="previewImageUrl"
        :aria-label="values.heading || 'Видео'"
      >
        <source :src="extras.video" />
      </video>
      <button
        v-if="!isPlayVideo"
        class="video-block__play"
        type="button"
        aria-label="Воспроизвести видео"
        @click="startVideo"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M8 5v14l11-7L8 5z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import type { IBlockDefaultProps } from '#sitis/internal/models/common';

const props = defineProps<IBlockDefaultProps>();
const $img = useImage();

const refVideo = ref<HTMLVideoElement | null>(null);
const isPlayVideo = ref(false);
const videoWrapRef = ref<HTMLDivElement>();
const isShowVideoFrame = ref(false);

const previewImageUrl = computed(() => {
  if (!props?.values?.image) {
    return;
  }
  return $img(String(props?.values?.image), {
    width: 1920,
    format: 'webp',
    quality: 80
  });
});

const startVideo = async () => {
  if (!props.extras.video || !refVideo.value) {
    return;
  }

  try {
    await refVideo.value.play();
    refVideo.value.controls = true;
    isPlayVideo.value = true;
  } catch (error) {
    console.error('Ошибка воспроизведения видео:', error);
  }
};

const { stop } = useIntersectionObserver(
  videoWrapRef,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      isShowVideoFrame.value = true;
      stop();
    }
  },
  {
    threshold: 0.1
  }
);
</script>

<style lang="scss" scoped>
.video-block {
	padding: 64px 0;
	padding-bottom: 0;
	margin-bottom: -32px;
	box-sizing: border-box;
}

.video-block__title {
	margin-bottom: 32px;
}

.video-block__video {
	position: relative;
	overflow: hidden;
	border-radius: 30px 30px 0 0;
	background: linear-gradient(0deg, rgb(0 0 0 / 20%) 0%, rgb(0 0 0 / 20%) 100%), lightgray 50% / cover no-repeat;

	video {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&::after {
		content: "";
		float: left;
		padding-top: 46.25%;
	}
}

.video-block__play {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 100px;
	height: 100px;
	border-radius: 100%;
	background-color: #F4F3F1;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	transition: background-color 0.2s ease, transform 0.2s ease;
	will-change: transform;

	&:hover {
		background-color: #E8E7E5;
		transform: translate(-50%, -50%) scale(1.05);
	}

	&:active {
		transform: translate(-50%, -50%) scale(0.95);
	}

	&:focus-visible {
		outline: 2px solid #373333;
		outline-offset: 2px;
	}

	svg {
		width: 24px;
		height: 24px;
		color: #373333;
	}
}

@media (max-width: 1023px) {
	.video-block {
		padding: 50px 0 0 0;
	}
	.video-block__title {
		margin-bottom: 24px;
	}
	.video-block__video {
		border-radius: 24px 24px 0 0;

		&:after {
			padding-top: 74.25%;
		}
	}
	.video-block__play {
		width: 50px;
		height: 50px;

		svg {
			width: 16px;
			height: 16px;
		}
	}
}
</style>
